import { Utilities } from './../../../../services/utilities/index';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Department, Client, Workflow, WorkflowType, GlobalApplicationFields } from '../../../../models';
import { DataSetField, ReportDataSetFieldDataType } from '../../../../models/custom-report';
import {
  UntypedFormGroup,
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  Validators
} from '@angular/forms';
import {
  DataService,
  WorkflowContextService,
  ClientService,
  WorkflowService,
  SecurityService
} from '../../../../services';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { ItemSearchOptionField } from 'src/app/components/filter-list/models/filterClasses';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { Actions } from '../../../../models';

@Component({
  selector: 'wm-client-department-detail',
  templateUrl: './client-department-detail.component.html',
  styleUrls: ['./client-department-detail.component.css'],
  standalone: false
})
export class ClientDepartmentDetailComponent implements OnInit {
  @Input() department: Department;
  @Input() showTitle = true;
  departmentId: string;

  @Input() redirectOnSave = true;
  @Input() showGoBackToList = true;
  @Input() client: Client = null;
  @Input() showControls = true;
  @Output() saved: EventEmitter<Department> = new EventEmitter<Department>();
  @Output() canceled: EventEmitter<Department> = new EventEmitter<Department>();
  @Input() form: UntypedFormGroup;

  nameCtl: AbstractControl;
  descriptionCtl: AbstractControl;
  headerTextCtl: AbstractControl;
  iconUrlCtl: AbstractControl;
  requireDocumentReviewCtl: AbstractControl;
  Utilities = Utilities;
  Object = Object;
  canConfigureInspectionApp = false;

  public loading = false;
  public saving = false;
  public testingApi = false;
  public testingFilter: string | null = null;
  public testingSortField: string | null = null;
  public showTestingControls$: Observable<boolean>;

  // Test results grid data
  public testResults: any = null;
  public testResultColumns: any[] = [];
  public testResultRows: any[] = [];
  public testResultRowsOriginal: any[] = [];
  public lastTestDescription: string = '';

  // Grid sorting and filtering state
  public gridSortColumn: string | null = null;
  public gridSortDescending: boolean = false;
  public gridFilters: { [key: string]: string } = {};

  // Custom field filter for testing
  public customFilterField: string = '';
  public customFilterValue: string = '';
  public customFilterOperator: string = 'contains';
  public testingCustomFilter: boolean = false;
  public availableFilterFields: any[] = [];

  // Simple search for testing
  public simpleSearchValue: string = '';
  public testingSimpleSearch: boolean = false;

  // Column selection for testing
  public availableColumns: Array<{name: string; label: string}> = [];
  public selectedColumns: string[] = [];
  public selectAllColumns: boolean = true;

  // Filter operators
  filterOperators = [
    { value: '==', label: 'Equals (=)' },
    { value: '!=', label: 'Not Equals (≠)' },
    { value: 'contains', label: 'Contains' },
    { value: 'startswith', label: 'Starts With' },
    { value: 'endswith', label: 'Ends With' },
    { value: '>', label: 'Greater Than (>)' },
    { value: '>=', label: 'Greater Than or Equal (≥)' },
    { value: '<', label: 'Less Than (<)' },
    { value: '<=', label: 'Less Than or Equal (≤)' }
  ];

  // Common sortable columns for testing
  sortableColumns = [
    { name: 'APPLICATION_NUMBER', label: 'Application #' },
    { name: 'APPLICATION_STATUS', label: 'Status' },
    { name: 'PERMIT_DESCRIPTION', label: 'Description' },
    { name: 'SUBMITTER', label: 'Submitter' },
    { name: 'CreateDate', label: 'Create Date' },
    { name: 'IssuedOn', label: 'Issued Date' },
    { name: 'WORKFLOW_NAME', label: 'Workflow Name' }
  ];

  // Predefined icon options (Material Design Icons)
  iconOptions = [
    { value: '', label: 'No Icon', icon: '' },
    { value: 'business', label: 'Building', icon: 'business' },
    { value: 'map', label: 'Map/Planning', icon: 'map' },
    { value: 'gavel', label: 'Code Enforcement', icon: 'gavel' },
    { value: 'construction', label: 'Construction', icon: 'construction' },
    {
      value: 'local_fire_department',
      label: 'Fire Department',
      icon: 'local_fire_department'
    },
    {
      value: 'security',
      label: 'Safety/Security',
      icon: 'security'
    },
    { value: 'eco', label: 'Environmental', icon: 'eco' },
    { value: 'add_road', label: 'Public Works', icon: 'add_road' },
    { value: 'groups', label: 'Human Resources', icon: 'groups' },
    { value: 'calculate', label: 'Finance', icon: 'calculate' },
    {
      value: 'assignment',
      label: 'Licensing',
      icon: 'assignment'
    },
    { value: 'park', label: 'Parks & Recreation', icon: 'park' },
    { value: 'directions_car', label: 'Transportation', icon: 'directions_car' },
    { value: 'handyman', label: 'Utilities', icon: 'handyman' },
    { value: 'home', label: 'Housing', icon: 'home' },
    {
      value: 'school',
      label: 'Education',
      icon: 'school'
    },
    {
      value: 'health_and_safety',
      label: 'Health Services',
      icon: 'health_and_safety'
    },
    {
      value: 'balance',
      label: 'Legal/Court',
      icon: 'balance'
    },
    { value: 'settings', label: 'General Services', icon: 'settings' },
    { value: 'dogIcon', label: 'Dog Control', icon: 'dogIcon' }
  ];

  constructor(
    private _service: DataService,
    private _clientService: ClientService,
    private _context: WorkflowContextService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _location: Location,
    private _toastr: ToastrService,
    private _formBuilder: UntypedFormBuilder,
    private _workflowService: WorkflowService,
    private _securityService: SecurityService
  ) {}

  ngOnInit() {
    this.departmentId = this._route.snapshot.params['id'];

    // Check if testing controls should be shown (dev environment + system admin)
    this.showTestingControls$ = this._securityService.isSystemAdministrator().pipe(
      map(isSystemAdmin => !environment.production && isSystemAdmin)
    );

    // Check if user can configure inspection app
    this._securityService.isLoginEntitled(Actions.CONFIGURE_INSPECTION_APP).subscribe(
      canConfigure => {
        this.canConfigureInspectionApp = canConfigure;
      }
    );

    // Initialize available filter fields with default sortable columns
    this.updateAvailableFilterFields();

    // Initialize available columns for column selection
    this.initializeAvailableColumns();

    if (!this.form) {
      this.buildForm();
    }

    if (!this.client) {
      this.client = this._context.client;
    }

    if (this.departmentId && this.departmentId !== 'add') {
      this.loading = true;
      this._clientService.getClientDepartment(this.departmentId).subscribe({
        next: department => {
          this.department = department;
          this.bindFormControls();
          this.loadDataSetColumns();
          this.loading = false;
        },
        error: error => {
          console.error('Failed to load department:', error);
          this._toastr.error('Failed to load department');
          // Create new department as fallback
          this.department = new Department();
          this.department.id = this.departmentId;
          this.department.clientId = this.client?.id;
          this.bindFormControls();
          this.loading = false;
        }
      });
    } else {
      this.department = new Department();
      this.department.clientId = this.client?.id;
      this.bindFormControls();
    }
  }


  buildForm() {
    this.form = this._formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', [Validators.required,Validators.maxLength(500)]],
      iconUrl: [''], // No validation needed for dropdown selection
      requireDocumentReview: [false],
      headerText: ['', [Validators.required]]
    });

    this.nameCtl = this.form.controls['name'];
    this.descriptionCtl = this.form.controls['description'];
    this.headerTextCtl = this.form.controls['headerText'];
    this.iconUrlCtl = this.form.controls['iconUrl'];
    this.requireDocumentReviewCtl = this.form.controls['requireDocumentReview'];
  }

  bindFormControls() {
    if (this.department) {
      this.nameCtl.setValue(this.department.name);
      this.descriptionCtl.setValue(this.department.description);
      this.headerTextCtl.setValue(this.department.headerText);
      this.iconUrlCtl.setValue(this.department.iconUrl);

      // Initialize application grid dataset with default columns if it doesn't exist
      if (!this.department.applicationGridDataSetId || this.department.applicationGridDataSetId === Utilities.EMPTY_GUID) {
        // Department doesn't have a dataset yet - we'll provide initial fields
        // This will be used by the dataset designer when it initializes
        this.getInitialFieldsForDepartment()
      }
      this.requireDocumentReviewCtl.setValue(this.department.requireDocumentReview || false);
    }
  }

  save() {
    if (this.form.valid) {
      this.saving = true;

      // Update department with form values
      this.department.name = this.nameCtl.value;
      this.department.description = this.descriptionCtl.value;
      this.department.headerText = this.headerTextCtl.value;
      this.department.iconUrl = this.iconUrlCtl.value;
      this.department.requireDocumentReview = this.requireDocumentReviewCtl.value;
      this.department.clientId = this.client?.id;

      this._clientService.saveClientDepartment(this.department).subscribe({
        next: savedDepartment => {
          this.department = savedDepartment;
          this.saved.emit(this.department);
          this._toastr.success(
            `Department ${
              this.departmentId && this.departmentId !== 'add'
                ? 'updated'
                : 'created'
            } successfully`
          );

          if (this.redirectOnSave) {
            this.goBackToList();
          }
          this.saving = false;
        },
        error: error => {
          console.error('Failed to save department:', error);
          this._toastr.error('Failed to save department');
          this.saving = false;
        }
      });
    } else {
      this.validateAllFormFields(this.form);
    }
  }

  cancel() {
    this.canceled.emit(this.department);
    if (this.redirectOnSave) {
      this.goBackToList();
    }
  }

  onDataSetSaved(dataSetId: string) {
    // Only save the department if the dataset ID is new or has changed
    if (this.department) {
      const isNewDataset = !this.department.applicationGridDataSetId || this.department.applicationGridDataSetId === Utilities.EMPTY_GUID;
      const datasetChanged = this.department.applicationGridDataSetId !== dataSetId;

      if (isNewDataset || datasetChanged) {
        this.department.applicationGridDataSetId = dataSetId;
        // Temporarily disable redirect to avoid navigating away when just saving dataset
        const originalRedirect = this.redirectOnSave;
        this.redirectOnSave = false;
        this._toastr.success('Dataset saved and linked to department');
        this.save();
        // Restore original redirect setting after save completes
        setTimeout(() => {
          this.redirectOnSave = originalRedirect;
        }, 0);
      } else {
        // Dataset was just updated, no need to save department
        this._toastr.success('Dataset saved');
      }

      // Reload columns from the updated dataset
      this.loadDataSetColumns();
    }
  }

  getStaticFiltersForDepartment(): ItemSearchOptionField[] {
    // Return department ID as a static filter to filter workflows
    if (!this.department || !this.department.id) {
      return [];
    }

    return [{
      title: 'DEPARTMENT_ID',
      options: [{
        title: 'DEPARTMENT_ID',
        selected: true,
        filterText: this.department.id,
        searchText: this.department.id,
        strOperator: 'eq'
      }]
    }];
  }

  getWorkflowsForDepartment = (): Observable<Workflow[]> => {
    return this._workflowService
      .getWorkflows(
        this._context.client,
        false,
        false,
        [
          WorkflowType.ContractorRegistration,
          WorkflowType.Renewable,
          WorkflowType.ContractorRenewal,
          WorkflowType.Permit,
          WorkflowType.Other
        ],
        this.department?.id
      );
  };

  getStaticFieldsForDepartment(): DataSetField[] {
    const fields: DataSetField[] = [];

    fields.push(...this.getInitialFieldsForDepartment());

    // Create documents field
    const documentsField = new DataSetField();
    documentsField.name = GlobalApplicationFields.DOCUMENTS_TO_REVIEW;
    documentsField.resultHeader = 'DocumentsToReview';
    documentsField.dataType = ReportDataSetFieldDataType.Integer;
    documentsField.isStatic = true;
    fields.push(documentsField);

    // Create total document count field (drives the blue paperclip icon in the staff
    // applications grid when non-review documents have been uploaded).
    const totalDocumentCountField = new DataSetField();
    totalDocumentCountField.name = GlobalApplicationFields.TOTAL_DOCUMENT_COUNT;
    totalDocumentCountField.resultHeader = 'TotalDocumentCount';
    totalDocumentCountField.dataType = ReportDataSetFieldDataType.Integer;
    totalDocumentCountField.isStatic = true;
    fields.push(totalDocumentCountField);

    // Create messages field
    const messagesField = new DataSetField();
    messagesField.name = GlobalApplicationFields.MESSAGES;
    messagesField.resultHeader = 'Messages';
    messagesField.dataType = ReportDataSetFieldDataType.Integer;
    messagesField.isStatic = true;
    fields.push(messagesField);

    // Create payment field
    const paymentField = new DataSetField();
    paymentField.name = GlobalApplicationFields.TOTAL_FEES_DUE_NUM;
    paymentField.resultHeader = 'FeesDue';
    paymentField.dataType = ReportDataSetFieldDataType.Integer;
    paymentField.isStatic = true;
    fields.push(paymentField);

    // Create notes field
    const notesField = new DataSetField();
    notesField.name = GlobalApplicationFields.APPLICATION_NOTES_COUNT;
    notesField.resultHeader = 'Notes';
    notesField.dataType = ReportDataSetFieldDataType.Integer;
    notesField.isStatic = true;
    fields.push(notesField);

    // Create assigned sign offs field
    const assignedSignOffsField = new DataSetField();
    assignedSignOffsField.name = GlobalApplicationFields.ASSIGNED_SIGN_OFFS_COUNT;
    assignedSignOffsField.resultHeader = 'Assigned SignOffs';
    assignedSignOffsField.dataType = ReportDataSetFieldDataType.Integer;
    assignedSignOffsField.isStatic = true;
    fields.push(assignedSignOffsField);

    // Create approved sign offs field
    const approvedSignOffsField = new DataSetField();
    approvedSignOffsField.name = GlobalApplicationFields.APPROVED_SIGN_OFFS_COUNT;
    approvedSignOffsField.resultHeader = 'Approved SignOffs';
    approvedSignOffsField.dataType = ReportDataSetFieldDataType.Integer;
    approvedSignOffsField.isStatic = true;
    fields.push(approvedSignOffsField);

    // Create disapproved sign offs field
    const disapprovedSignOffsField = new DataSetField();
    disapprovedSignOffsField.name = GlobalApplicationFields.DISAPPROVED_SIGN_OFFS_COUNT;
    disapprovedSignOffsField.resultHeader = 'Disapproved SignOffs';
    disapprovedSignOffsField.dataType = ReportDataSetFieldDataType.Integer;
    disapprovedSignOffsField.isStatic = true;
    fields.push(disapprovedSignOffsField);

    // Create Inspections field
    const inspectionsField = new DataSetField();
    inspectionsField.name = GlobalApplicationFields.INSPECTIONS_COUNT;
    inspectionsField.resultHeader = 'InspectionsCount';
    inspectionsField.dataType = ReportDataSetFieldDataType.Integer;
    inspectionsField.isStatic = true;
    fields.push(inspectionsField);

    // Create ApplicantSubmitted field
    const applicantSubmittedField = new DataSetField();
    applicantSubmittedField.name = GlobalApplicationFields.APPLICANT_SUBMITTED;
    applicantSubmittedField.resultHeader = 'ApplicantSubmitted';
    applicantSubmittedField.dataType = ReportDataSetFieldDataType.String;
    applicantSubmittedField.isStatic = true;
    fields.push(applicantSubmittedField);

    // Create CreateDate field
    const createDateField = new DataSetField();
    createDateField.name = GlobalApplicationFields.CREATE_DATE;
    createDateField.resultHeader = 'Create Date';
    createDateField.dataType = ReportDataSetFieldDataType.Date;
    createDateField.isStatic = true;
    fields.push(createDateField);

    return fields;
  }

  getInitialFieldsForDepartment(): DataSetField[] {
    // Only return initial fields if the department doesn't have a dataset yet
    if (this.department?.applicationGridDataSetId && this.department.applicationGridDataSetId !== Utilities.EMPTY_GUID) {
      return [];
    }

    const fields: DataSetField[] = [];

    // App # (APPLICATION_NUMBER)
    const appNumberField = new DataSetField();
    appNumberField.name = GlobalApplicationFields.APPLICATION_NUMBER;
    appNumberField.resultHeader = 'App #';
    appNumberField.dataType = ReportDataSetFieldDataType.String;
    appNumberField.isStatic = false;
    fields.push(appNumberField);

    // App Type (WORKFLOW_NAME)
    const appTypeField = new DataSetField();
    appTypeField.name = GlobalApplicationFields.WORKFLOW_NAME;
    appTypeField.resultHeader = 'App Type';
    appTypeField.dataType = ReportDataSetFieldDataType.String;
    appTypeField.isStatic = false;
    fields.push(appTypeField);

    // App Date (ApplicationSubmittedOn)
    const appDateField = new DataSetField();
    appDateField.name = GlobalApplicationFields.APPLICATION_SUBMITTED_ON;
    appDateField.resultHeader = 'App Date';
    appDateField.dataType = ReportDataSetFieldDataType.Date;
    appDateField.isStatic = false;
    fields.push(appDateField);

    // Issue Date (IssuedOn)
    const issueDateField = new DataSetField();
    issueDateField.name = GlobalApplicationFields.ISSUED_ON;
    issueDateField.resultHeader = 'Issue Date';
    issueDateField.dataType = ReportDataSetFieldDataType.Date;
    issueDateField.isStatic = false;
    fields.push(issueDateField);

    // Site Address (PARCEL_ADDRESS) - This is a non-static field that will need to be workflow-specific
    const siteAddressField = new DataSetField();
    siteAddressField.name = 'PARCEL_ADDRESS';
    siteAddressField.resultHeader = 'Site Address';
    siteAddressField.dataType = ReportDataSetFieldDataType.String;
    siteAddressField.isStatic = false;
    fields.push(siteAddressField);

    // Applicant (SUBMITTER)
    const applicantField = new DataSetField();
    applicantField.name = GlobalApplicationFields.SUBMITTER;
    applicantField.resultHeader = 'Applicant';
    applicantField.dataType = ReportDataSetFieldDataType.String;
    applicantField.isStatic = false;
    fields.push(applicantField);

    // Owner (PARCEL_OWNER) - This is a non-static field that will need to be workflow-specific
    const ownerField = new DataSetField();
    ownerField.name = 'PARCEL_OWNER';
    ownerField.resultHeader = 'Owner';
    ownerField.dataType = ReportDataSetFieldDataType.String;
    ownerField.isStatic = false;
    fields.push(ownerField);

    // Status (APPLICATION_STATUS)
    const statusField = new DataSetField();
    statusField.name = GlobalApplicationFields.APPLICATION_STATUS;
    statusField.resultHeader = 'Status';
    statusField.dataType = ReportDataSetFieldDataType.String;
    statusField.isStatic = false;
    fields.push(statusField);

    // Current Activity (CURRENT_ACTIVITY)
    const currentActivityField = new DataSetField();
    currentActivityField.name = GlobalApplicationFields.CURRENT_ACTIVITY;
    currentActivityField.resultHeader = 'Current Activity';
    currentActivityField.dataType = ReportDataSetFieldDataType.String;
    currentActivityField.isStatic = false;
    fields.push(currentActivityField);

    return fields;
  }

  goBackToList() {
    if (this.client) {
      this._router.navigate([
        '/admin/jurisdiction',
        this.client.id,
        'settings',
        'departments'
      ]);
    } else {
      this._location.back();
    }
  }

  delete() {
    if (
      this.department?.id &&
      confirm('Are you sure you want to delete this department?')
    ) {
      this.saving = true;
      // TODO: Implement actual delete when API is available
      setTimeout(() => {
        this._toastr.success('Department deleted successfully');
        this.goBackToList();
        this.saving = false;
      }, 1000);
    }
  }

  private validateAllFormFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  getValidationMessage(control: AbstractControl): string {
    if (control.errors) {
      if (control.errors['required']) {
        return 'This field is required';
      }
      if (control.errors['maxlength']) {
        return `Maximum length is ${control.errors['maxlength'].requiredLength}`;
      }
    }
    return '';
  }

  hasValidationError(control: AbstractControl): boolean {
    return control.invalid && (control.dirty || control.touched);
  }

  testGetDepartmentApplications() {
    if (!this.department?.id) {
      console.error('No department ID available');
      return;
    }

    this.testingApi = true;
    this.lastTestDescription = 'Get Department Applications (All)';
    console.log('Testing GetDepartmentApplications API for department:', this.department.id);
    console.log('Selected columns:', this.getColumnsToInclude());

    this._workflowService.getDepartmentApplications(
      this.department.id,
      { pageNumber: 1, maxRows: 100 },
      this.getColumnsToInclude()
    ).subscribe({
      next: (result) => {
        this.testingApi = false;
        console.log('GetDepartmentApplications API Response:', result);
        console.log('Dataset:', result.dataSet);
        console.log('Columns:', result.dataSetResult?.columns);
        console.log('Rows:', result.dataSetResult?.rows);
        console.log('Total Rows:', result.dataSetResult?.rows?.length);

        // Store results for grid display
        this.testResults = result;
        this.testResultColumns = result.dataSet?.dataSetConfig?.fields || [];
        this.testResultRows = result.dataSetResult?.rows || [];
        this.testResultRowsOriginal = [...(result.dataSetResult?.rows || [])];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();

        this._toastr.success(`Retrieved ${result.dataSetResult?.rows?.length || 0} applications. Check results grid below.`);
      },
      error: (error) => {
        this.testingApi = false;
        console.error('GetDepartmentApplications API Error:', error);
        this._toastr.error(`API call failed: ${error.message || 'Unknown error'}. Check console for details.`);

        // Clear results on error
        this.testResults = null;
        this.testResultColumns = [];
        this.testResultRows = [];
        this.testResultRowsOriginal = [];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();
      }
    });
  }

  testFilter(filterName: string) {
    if (!this.department?.id) {
      console.error('No department ID available');
      return;
    }

    this.testingFilter = filterName;
    const filterLabel = this.getFilterLabel(filterName);
    this.lastTestDescription = `Filter Test: ${filterLabel}`;
    console.log(`Testing ${filterName} filter for department:`, this.department.id);

    // Handle MY_APPLICATIONS filter specially
    let filterInput;
    if (filterName === 'MY_APPLICATIONS') {
      const currentUserId = this._context.user?.id;
      if (!currentUserId) {
        console.error('No user ID available for MY_APPLICATIONS filter');
        this._toastr.error('Cannot test My Applications: User ID not found');
        this.testingFilter = null;
        return;
      }

      filterInput = {
        pageNumber: 1,
        maxRows: 100,
        inputValues: [
          {
            title: 'submitter_Id',
            searchText: currentUserId,
            filterText: currentUserId,
            strOperator: '==',
            inputType: 'text'
          }
        ]
      };
      console.log('MY_APPLICATIONS filter - Using submitterId with current user ID:', currentUserId);
    } else {
      // Create a test request with the specific filter
      filterInput = {
        pageNumber: 1,
        maxRows: 100,
        inputValues: [
          {
            title: filterName,
            searchText: '1',
            filterText: filterName,
            strOperator: '>',
            inputType: 'number'
          }
        ]
      };
    }

    console.log('Selected columns:', this.getColumnsToInclude());

    this._workflowService.getDepartmentApplications(
      this.department.id,
      filterInput,
      this.getColumnsToInclude()
    ).subscribe({
      next: (result) => {
        this.testingFilter = null;
        console.log(`${filterName} Filter Test Response:`, result);
        console.log('Dataset:', result.dataSet);
        console.log('Columns:', result.dataSetResult?.columns);
        console.log('Rows:', result.dataSetResult?.rows);
        console.log('Total Rows:', result.dataSetResult?.rows?.length);
        console.log('Filter Input Used:', filterInput);

        // Store results for grid display
        this.testResults = result;
        this.testResultColumns = result.dataSet?.dataSetConfig?.fields || [];
        this.testResultRows = result.dataSetResult?.rows || [];
        this.testResultRowsOriginal = [...(result.dataSetResult?.rows || [])];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};

        const rowCount = result.dataSetResult?.rows?.length || 0;
        this._toastr.success(
          `${filterLabel}: Found ${rowCount} application(s). Check results grid below.`,
          'Filter Test Success'
        );
      },
      error: (error) => {
        this.testingFilter = null;
        console.error(`${filterName} Filter Test Error:`, error);
        this._toastr.error(
          `Filter test failed: ${error.message || 'Unknown error'}. Check console for details.`,
          'Filter Test Failed'
        );

        // Clear results on error
        this.testResults = null;
        this.testResultColumns = [];
        this.testResultRows = [];
        this.testResultRowsOriginal = [];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();
      }
    });
  }

  getFilterLabel(filterName: string): string {
    const labels: { [key: string]: string } = {
      'REQUIRED_DOCUMENTS': 'Required Documents',
      'GENERATED_PERMITS': 'Generated Permits',
      'REGISTRATIONS_TO_RENEW': 'Registrations to Renew',
      'REGISTRATIONS_ABOUT_TO_EXPIRE': 'Registrations About to Expire',
      'INSPECTIONS': 'Upcoming Inspections',
      'INSPECTIONS_TO_SCHEDULE': 'Inspections to Schedule',
      'INSPECTIONS_TO_COMPLETE': 'Inspections to Complete',
      'UPCOMING_INSPECTIONS_TO_COMPLETE': 'Upcoming Inspections to Complete',
      'PENDING_PAYMENTS': 'Pending Payments',
      'MY_APPLICATIONS': 'My Applications',
      'SHARED_BY_ME': 'Shared By Me',
      'SHARED_WITH_ME': 'Shared With Me',
      'RELEVANT_TO_USER': 'Relevant To User',
      'NEEDS_MY_ATTENTION': 'Needs My Attention'
    };
    return labels[filterName] || filterName;
  }

  testCustomFieldFilter() {
    if (!this.department?.id) {
      console.error('No department ID available');
      return;
    }

    if (!this.customFilterField || !this.customFilterValue) {
      this._toastr.warning('Please select a field and enter a filter value');
      return;
    }

    this.testingCustomFilter = true;
    this.lastTestDescription = `Custom Filter: ${this.customFilterField} ${this.customFilterOperator} "${this.customFilterValue}"`;
    console.log(`Testing custom field filter for department:`, this.department.id);

    // Create a test request with the custom field filter
    const filterInput = {
      pageNumber: 1,
      maxRows: 100,
      inputValues: [
        {
          title: this.customFilterField,
          searchText: this.customFilterValue,
          filterText: this.customFilterValue,
          strOperator: this.customFilterOperator,
          inputType: 'text'
        }
      ]
    };

    console.log('Selected columns:', this.getColumnsToInclude());

    this._workflowService.getDepartmentApplications(
      this.department.id,
      filterInput,
      this.getColumnsToInclude()
    ).subscribe({
      next: (result) => {
        this.testingCustomFilter = false;
        console.log(`Custom Field Filter Test Response:`, result);
        console.log('Dataset:', result.dataSet);
        console.log('Columns:', result.dataSetResult?.columns);
        console.log('Rows:', result.dataSetResult?.rows);
        console.log('Total Rows:', result.dataSetResult?.rows?.length);
        console.log('Filter Input Used:', filterInput);

        // Store results for grid display
        this.testResults = result;
        this.testResultColumns = result.dataSet?.dataSetConfig?.fields || [];
        this.testResultRows = result.dataSetResult?.rows || [];
        this.testResultRowsOriginal = [...(result.dataSetResult?.rows || [])];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();

        const rowCount = result.dataSetResult?.rows?.length || 0;
        this._toastr.success(
          `Found ${rowCount} application(s) with custom filter. Check results grid below.`,
          'Custom Filter Test Success'
        );
      },
      error: (error) => {
        this.testingCustomFilter = false;
        console.error(`Custom Field Filter Test Error:`, error);
        this._toastr.error(
          `Filter test failed: ${error.message || 'Unknown error'}. Check console for details.`,
          'Custom Filter Test Failed'
        );

        // Clear results on error
        this.testResults = null;
        this.testResultColumns = [];
        this.testResultRows = [];
        this.testResultRowsOriginal = [];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();
      }
    });
  }

  updateAvailableFilterFields(): void {
    // Update available filter fields based on current test result columns
    if (this.testResultColumns && this.testResultColumns.length > 0) {
      this.availableFilterFields = this.testResultColumns.map(col => ({
        name: col.name,
        resultHeader: col.resultHeader,
        label: col.resultHeader
      }));
    } else {
      // Return common fields as fallback
      this.availableFilterFields = this.sortableColumns.map(col => ({
        name: col.name,
        resultHeader: col.name,
        label: col.label
      }));
    }
  }

  testSimpleSearch() {
    if (!this.department?.id) {
      console.error('No department ID available');
      return;
    }

    if (!this.simpleSearchValue || !this.simpleSearchValue.trim()) {
      this._toastr.warning('Please enter a search value');
      return;
    }

    this.testingSimpleSearch = true;
    this.lastTestDescription = `Simple Search: "${this.simpleSearchValue}"`;
    console.log(`Testing simple search for department:`, this.department.id);

    // Get all available fields to search across
    const fieldsToSearch = this.availableFilterFields.length > 0
      ? this.availableFilterFields
      : this.sortableColumns;

    // Create filter input that searches across all fields
    const filterInput = {
      pageNumber: 1,
      maxRows: 100,
      inputValues: fieldsToSearch.map(field => ({
        title: field.name || field.resultHeader,
        searchText: '',
        filterText: this.simpleSearchValue.trim(),
        strOperator: 'contains',
        inputType: 'text',
        searchOptionMethod: 'simpleSearch'
      }))
    };

    console.log('Selected columns:', this.getColumnsToInclude());

    this._workflowService.getDepartmentApplications(
      this.department.id,
      filterInput,
      this.getColumnsToInclude()
    ).subscribe({
      next: (result) => {
        this.testingSimpleSearch = false;
        console.log(`Simple Search Test Response:`, result);
        console.log('Dataset:', result.dataSet);
        console.log('Columns:', result.dataSetResult?.columns);
        console.log('Rows:', result.dataSetResult?.rows);
        console.log('Total Rows:', result.dataSetResult?.rows?.length);
        console.log('Filter Input Used:', filterInput);

        // Store results for grid display
        this.testResults = result;
        this.testResultColumns = result.dataSet?.dataSetConfig?.fields || [];
        this.testResultRows = result.dataSetResult?.rows || [];
        this.testResultRowsOriginal = [...(result.dataSetResult?.rows || [])];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();

        const rowCount = result.dataSetResult?.rows?.length || 0;
        this._toastr.success(
          `Found ${rowCount} application(s) matching "${this.simpleSearchValue}". Check results grid below.`,
          'Simple Search Test Success'
        );
      },
      error: (error) => {
        this.testingSimpleSearch = false;
        console.error(`Simple Search Test Error:`, error);
        this._toastr.error(
          `Simple search failed: ${error.message || 'Unknown error'}. Check console for details.`,
          'Simple Search Test Failed'
        );

        // Clear results on error
        this.testResults = null;
        this.testResultColumns = [];
        this.testResultRows = [];
        this.testResultRowsOriginal = [];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();
      }
    });
  }

  testSorting(columnName: string, descending: boolean) {
    if (!this.department?.id) {
      console.error('No department ID available');
      return;
    }

    this.testingSortField = `${columnName}_${descending ? 'DESC' : 'ASC'}`;
    const direction = descending ? 'descending' : 'ascending';
    const columnLabel = this.sortableColumns.find(c => c.name === columnName)?.label || columnName;
    this.lastTestDescription = `Sort Test: ${columnLabel} (${direction})`;
    console.log(`Testing sorting by ${columnName} (${direction}) for department:`, this.department.id);
    console.log('Selected columns:', this.getColumnsToInclude());

    this._workflowService.getDepartmentApplications(
      this.department.id,
      { pageNumber: 1, maxRows: 100 },
      this.getColumnsToInclude(),
      descending,
      columnName
    ).subscribe({
      next: (result) => {
        this.testingSortField = null;
        console.log(`Sort Test (${columnName} ${direction}) Response:`, result);
        console.log('Dataset:', result.dataSet);
        console.log('Columns:', result.dataSetResult?.columns);
        console.log('Rows:', result.dataSetResult?.rows);
        console.log('Total Rows:', result.dataSetResult?.rows?.length);

        // Display first few values of the sorted column for verification
        const sortColumn = result.dataSetResult?.columns?.find(c => c.name === columnName);
        if (sortColumn) {
          const firstValues = result.dataSetResult?.rows?.slice(0, 5).map(row => {
            const cell = row.cells.find(c => c.name === columnName);
            return cell?.value || 'N/A';
          });
          console.log(`First 5 values of ${columnName}:`, firstValues);
        }

        // Store results for grid display
        this.testResults = result;
        this.testResultColumns = result.dataSet?.dataSetConfig?.fields || [];
        this.testResultRows = result.dataSetResult?.rows || [];
        this.testResultRowsOriginal = [...(result.dataSetResult?.rows || [])];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();

        const rowCount = result.dataSetResult?.rows?.length || 0;
        this._toastr.success(
          `Retrieved ${rowCount} application(s) sorted by ${columnLabel} (${direction}). Check results grid below.`,
          'Sort Test Success'
        );
      },
      error: (error) => {
        this.testingSortField = null;
        console.error(`Sort Test (${columnName} ${direction}) Error:`, error);
        this._toastr.error(
          `Sort test failed: ${error.message || 'Unknown error'}. Check console for details.`,
          'Sort Test Failed'
        );

        // Clear results on error
        this.testResults = null;
        this.testResultColumns = [];
        this.testResultRows = [];
        this.testResultRowsOriginal = [];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();
      }
    });
  }

  getCellValue(row: any, columnName: string): any {
    // C# serialization uses two different patterns:
    // 1. With underscores: First word section lowercase except last char, subsequent sections uppercase
    //    e.g., "ALL_MESSAGES" -> "alL_MESSAGES", "PERMIT_DESCRIPTION" -> "permiT_DESCRIPTION"
    // 2. Without underscores: Normal camelCase (lowercase first character)
    //    e.g., "CreateDate" -> "createDate", "ApplicationId" -> "applicationId"

    let convertedKey: string;

    if (columnName.includes('_')) {
      // Has underscores - use special pattern
      const parts = columnName.split('_');
      convertedKey = parts.map((part, index) => {
        if (index === 0) {
          // First part: lowercase all except last character
          if (part.length === 0) return part;
          if (part.length === 1) return part.toLowerCase();
          return part.slice(0, -1).toLowerCase() + part.slice(-1).toUpperCase();
        } else {
          // Subsequent parts: keep all uppercase
          return part.toUpperCase();
        }
      }).join('_');
    } else {
      // No underscores - use normal camelCase (lowercase first character)
      convertedKey = columnName.charAt(0).toLowerCase() + columnName.slice(1);
    }

    const cell = row?.values?.[convertedKey];
    return cell ?? '';
  }

  clearTestResults() {
    this.testResults = null;
    this.testResultColumns = [];
    this.testResultRows = [];
    this.testResultRowsOriginal = [];
    this.lastTestDescription = '';
    this.gridSortColumn = null;
    this.gridSortDescending = false;
    this.gridFilters = {};
    this.updateAvailableFilterFields();
  }

  sortGridByColumn(columnName: string) {
    if (this.gridSortColumn === columnName) {
      // Toggle sort direction if same column
      this.gridSortDescending = !this.gridSortDescending;
    } else {
      // New column, sort ascending
      this.gridSortColumn = columnName;
      this.gridSortDescending = false;
    }

    this.applyGridSortAndFilter();
  }

  onGridFilterChange(columnName: string, filterValue: string) {
    if (filterValue) {
      this.gridFilters[columnName] = filterValue.toLowerCase();
    } else {
      delete this.gridFilters[columnName];
    }

    this.applyGridSortAndFilter();
  }

  applyGridSortAndFilter() {
    // Start with original data
    let filteredRows = [...this.testResultRowsOriginal];

    // Apply filters
    if (Object.keys(this.gridFilters).length > 0) {
      filteredRows = filteredRows.filter(row => {
        return Object.keys(this.gridFilters).every(columnName => {
          const cellValue = this.getCellValue(row, columnName);
          const filterValue = this.gridFilters[columnName];
          return cellValue?.toString().toLowerCase().includes(filterValue);
        });
      });
    }

    // Apply sorting
    if (this.gridSortColumn) {
      filteredRows.sort((a, b) => {
        const aValue = this.getCellValue(a, this.gridSortColumn!);
        const bValue = this.getCellValue(b, this.gridSortColumn!);

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        // Try numeric comparison first
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return this.gridSortDescending ? bNum - aNum : aNum - bNum;
        }

        // Fall back to string comparison
        const aStr = aValue.toString().toLowerCase();
        const bStr = bValue.toString().toLowerCase();
        const comparison = aStr.localeCompare(bStr);
        return this.gridSortDescending ? -comparison : comparison;
      });
    }

    this.testResultRows = filteredRows;
  }

  getSortIcon(columnName: string): string {
    if (this.gridSortColumn !== columnName) {
      return 'fas fa-sort text-muted';
    }
    return this.gridSortDescending ? 'fas fa-sort-down' : 'fas fa-sort-up';
  }

  clearGridFilters() {
    this.gridFilters = {};
    this.applyGridSortAndFilter();
  }

  /**
   * Initialize available columns for selection in the testing UI
   * This sets up defaults but should be replaced by loadDataSetColumns when the dataset is loaded
   */
  initializeAvailableColumns(): void {
    // Start with empty columns - will be populated when dataset loads
    this.availableColumns = [];
    this.selectedColumns = [];
    this.selectAllColumns = true;
  }

  /**
   * Load available columns from the department's dataset
   */
  loadDataSetColumns(): void {
    // Check if department has a dataset configured
    if (!this.department?.applicationGridDataSetId || this.department.applicationGridDataSetId === Utilities.EMPTY_GUID) {
      // No dataset configured yet - use empty columns
      this.availableColumns = [];
      this.selectedColumns = [];
      this.selectAllColumns = true;
      return;
    }

    // Load the dataset to get its fields
    this._workflowService.getDataSet(this.department.applicationGridDataSetId, false).subscribe({
      next: (dataSet) => {
        if (dataSet) {
          // Use published config if available, otherwise use draft config
          const config = dataSet.publishedDataSetConfig || dataSet.dataSetConfig;

          if (config?.fields && config.fields.length > 0) {
            // Map dataset fields to available columns
            this.availableColumns = config.fields.map(field => ({
              name: field.name,
              label: field.label || field.resultHeader || field.name
            }));

            // By default, select all columns
            this.selectedColumns = this.availableColumns.map(col => col.name);
            this.selectAllColumns = true;

            console.log('Loaded columns from dataset:', this.availableColumns.length, 'columns');
          } else {
            console.warn('Dataset has no fields configured');
            this.availableColumns = [];
            this.selectedColumns = [];
          }
        }
      },
      error: (error) => {
        console.error('Failed to load dataset for columns:', error);
        // On error, just use empty columns
        this.availableColumns = [];
        this.selectedColumns = [];
      }
    });
  }

  /**
   * Toggle column selection
   */
  toggleColumnSelection(columnName: string): void {
    const index = this.selectedColumns.indexOf(columnName);
    if (index > -1) {
      this.selectedColumns.splice(index, 1);
    } else {
      this.selectedColumns.push(columnName);
    }
    this.updateSelectAllState();
  }

  /**
   * Check if a column is selected
   */
  isColumnSelected(columnName: string): boolean {
    return this.selectedColumns.includes(columnName);
  }

  /**
   * Toggle all columns selection
   */
  toggleAllColumns(): void {
    if (this.selectAllColumns) {
      this.selectedColumns = this.availableColumns.map(col => col.name);
    } else {
      this.selectedColumns = [];
    }
  }

  /**
   * Update the select all state based on current selections
   */
  updateSelectAllState(): void {
    this.selectAllColumns = this.selectedColumns.length === this.availableColumns.length;
  }

  /**
   * Get the selected columns array to pass to API (returns undefined if all selected)
   */
  getColumnsToInclude(): string[] | undefined {
    // If all columns are selected, don't pass the parameter (undefined means return all)
    if (this.selectAllColumns || this.selectedColumns.length === 0) {
      return undefined;
    }
    return this.selectedColumns;
  }
}
